"use client";

import Link from "next/link";
import React from "react";
import clsx from "clsx";
import {
  LuMessagesSquare,
  LuBookOpen,
  LuPlane,
  LuDollarSign,
  LuBriefcaseBusiness,
  LuUsers,
  LuGlobe,
} from "react-icons/lu";

type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>;

export type Channel = {
  label: string;
  value: string;
  href?: string;
  description?: string;
  icon?: IconType;
  count?: number | string;
};

type Stats = {
  totalPosts?: number | string;
  activeUsers?: number | string;
  onlineNow?: number | string;
};

type SidebarProps = {
  active?: string;
  channels?: Channel[];
  onSelect?: (value: string) => void;
  className?: string;
  innerClassName?: string;
  showStats?: boolean;
  stats?: Stats;
};

const DEFAULT_CHANNELS: Channel[] = [
  {
    label: "All Discussions",
    value: "all",
    href: "/forum",
    description: "All topics",
    icon: LuMessagesSquare,
    count: 0,
  },
  {
    label: "Study & Academics",
    value: "study-academics",
    href: "/forum?tab=study-academics",
    description: "Programs, courses, research",
    icon: LuBookOpen,
    count: 0,
  },
  {
    label: "Visa & Immigration",
    value: "visa-immigration",
    href: "/forum?tab=visa-immigration",
    description: "Visa processes, documents",
    icon: LuGlobe,
    count: 0,
  },
  {
    label: "Travel & Living",
    value: "travel-living",
    href: "/forum?tab=travel-living",
    description: "Accommodation, transport",
    icon: LuPlane,
    count: 0,
  },
  {
    label: "Finance & Scholarships",
    value: "finance-scholarships",
    href: "/forum?tab=finance-scholarships",
    description: "Funding, budgeting",
    icon: LuDollarSign,
    count: 0,
  },
  {
    label: "Career & Jobs",
    value: "career-jobs",
    href: "/forum?tab=career-jobs",
    description: "Internships, job search",
    icon: LuBriefcaseBusiness,
    count: 0,
  },
  {
    label: "Social & Events",
    value: "social-events",
    href: "/forum?tab=social-events",
    description: "Meetups, social life",
    icon: LuUsers,
    count: 0,
  },
];

export default function ForumSidebar({
  active,
  channels = DEFAULT_CHANNELS,
  onSelect,
  className,
  innerClassName,
  showStats = true,
  stats = { totalPosts: 0, activeUsers: "1,247", onlineNow: 89 },
}: SidebarProps) {
  return (
    <div
      className={clsx(
        "bg-gray-50 dark:bg-black/40 backdrop-blur-[2px]",
        className,
      )}
    >
      <div className={clsx("px-4 py-5", innerClassName)}>
        <div className="px-2 mb-2 text-xs font-semibold tracking-wide uppercase text-gray-500 dark:text-gray-300/80">
          Channels
        </div>

        <nav className="space-y-1">
          {channels.map((c) => {
            const isActive = active === c.value;
            const Icon = c.icon ?? LuMessagesSquare;

            const itemBase =
              "group flex items-start gap-2.5 rounded-lg px-2.5 py-2 transition ring-1 ring-inset text-left";
            const itemState = isActive
              ? "bg-gray-200 dark:bg-white/8 ring-gray-300 dark:ring-white/15"
              : "hover:bg-gray-100 dark:hover:bg-white/5 ring-transparent";

            const Inner = (
              <>
                <span
                  aria-hidden
                  className={clsx(
                    "w-1 self-stretch rounded-md",
                    isActive
                      ? "bg-black"
                      : "bg-transparent group-hover:bg-gray-300 dark:group-hover:bg-white/10",
                  )}
                />
                <span className="h-8 w-8 grid place-items-center rounded-lg bg-gray-200 dark:bg-white/8 text-gray-700 dark:text-gray-200 shrink-0">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="flex-1 w-full">
                  <span className="block text-sm font-semibold whitespace-normal wrap-break-word leading-snug">
                    {c.label}
                  </span>
                  {c.description && (
                    <span className="mt-0.5 block text-xs text-gray-500 whitespace-normal wrap-break-word leading-snug">
                      {c.description}
                    </span>
                  )}
                </span>
                <span className="ml-2 shrink-0 rounded-full bg-gray-300 dark:bg-white/10 text-gray-700 dark:text-gray-200 text-[10px] font-semibold px-1.5 py-0.5">
                  {c.count ?? 0}
                </span>
              </>
            );

            return c.href ? (
              <Link
                key={c.value}
                href={c.href}
                aria-current={isActive ? "page" : undefined}
                className={clsx(itemBase, itemState)}
              >
                {Inner}
              </Link>
            ) : (
              <button
                key={c.value}
                type="button"
                onClick={() => onSelect?.(c.value)}
                aria-pressed={isActive}
                className={clsx(itemBase, itemState, "w-full")}
              >
                {Inner}
              </button>
            );
          })}
        </nav>

        {showStats && (
          <div className="mt-6 rounded-xl bg-gray-100 dark:bg-white/5 ring-1 ring-inset ring-gray-300 dark:ring-white/10 p-4 text-left">
            <h3 className="text-sm font-semibold mb-3">Community Stats</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-gray-600 dark:text-gray-300">
                  Total Posts
                </dt>
                <dd className="font-semibold">{stats.totalPosts ?? "—"}</dd>
              </div>
              {/* <div className="flex items-center justify-between">
                <dt className="text-gray-600 dark:text-gray-300">
                  Active Users
                </dt>
                <dd className="font-semibold">{stats.activeUsers ?? "—"}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-gray-600 dark:text-gray-300">Online Now</dt>
                <dd className="font-semibold text-green-600 dark:text-green-300">
                  {stats.onlineNow ?? "—"}
                </dd>
              </div> */}
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
